import React, { useState, useRef, useEffect } from 'react';
import { requestPasswordReset } from '../services/UserService';
import { useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

// Error boundary to prevent reCAPTCHA runtime errors from crashing the page (e.g., Timeout (B))
class RecaptchaBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('reCAPTCHA runtime error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <p className='form-error'>
          reCAPTCHA failed to load. Please check your network/ad blockers and
          try again.
        </p>
      );
    }
    return this.props.children;
  }
}

export default function ForgotPasswordPage() {
  const location = useLocation();
  const prefillEmail = location.state?.email || '';
  const [email, setEmail] = useState(prefillEmail);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false); // render captcha after mount to avoid StrictMode race
  const [fallbackVisible, setFallbackVisible] = useState(false); // switch to checkbox on errors
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => setMounted(true), []);

  // DEV-ONLY: Suppress reCAPTCHA Timeout (B) from crashing the overlay in development
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const onErr = (e) => {
      const msg = e?.message || '';
      if (typeof msg === 'string' && msg.includes('Timeout (B)')) {
        e.preventDefault?.();
        e.stopImmediatePropagation?.();
        // eslint-disable-next-line no-console
        console.warn('Suppressed reCAPTCHA runtime error:', msg);
      }
    };
    const onRej = (e) => {
      const reason = e?.reason;
      const msg = typeof reason === 'string' ? reason : reason?.message;
      if (msg && msg.includes('Timeout (B)')) {
        e.preventDefault?.();
        // eslint-disable-next-line no-console
        console.warn('Suppressed reCAPTCHA unhandled rejection:', msg);
      }
    };
    window.addEventListener('error', onErr, true);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
      window.removeEventListener('error', onErr, true);
      window.removeEventListener('unhandledrejection', onRej);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      setSubmitting(true);
      if (!recaptchaRef.current) {
        setError('CAPTCHA not ready. Please reload the page and try again.');
        return;
      }

      let token = null;

      if (fallbackVisible) {
        // Visible checkbox mode: rely on user interaction
        token = recaptchaRef.current.getValue?.();
        if (!token) {
          setError('Please complete the CAPTCHA before submitting.');
          return;
        }
      } else {
        // Invisible mode: execute programmatically
        try {
          token = await recaptchaRef.current.executeAsync();
        } catch (captchaErr) {
          // Switch to visible checkbox mode on persistent failures
          setFallbackVisible(true);
          setError(
            'reCAPTCHA timed out or failed to load. Please verify using the checkbox below and submit again.'
          );
          return;
        } finally {
          // Always reset so it can be reused on retry
          try {
            recaptchaRef.current.reset();
          } catch {}
        }
      }

      if (!token) {
        setError('reCAPTCHA validation failed. Please try again.');
        return;
      }

      await requestPasswordReset(email, token);
      setMessage(
        'If an account exists, a reset link has been sent to your email.'
      );
      // Clear captcha selection in visible mode after success
      try {
        recaptchaRef.current.reset?.();
      } catch {}
    } catch (err) {
      setError(
        'Failed to send reset email. Please complete the CAPTCHA and try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='container' style={{ paddingTop: 24 }}>
      <h2>Forgot Password?</h2>
      <form onSubmit={handleSubmit} className='form' style={{ maxWidth: 420 }}>
        <input
          className='input'
          type='email'
          placeholder='Enter your email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {/* Render the CAPTCHA only after mount to avoid StrictMode double-mount issues */}
        {mounted && (
          <RecaptchaBoundary>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              size={fallbackVisible ? 'normal' : 'invisible'}
              badge='bottomright'
              useRecaptchaNet
              onChange={(val) => {
                // When in visible mode, user might complete the checkbox before submitting
                if (fallbackVisible && !val) {
                  setError('Please complete the CAPTCHA before submitting.');
                }
              }}
              onExpired={() => {
                setError('reCAPTCHA expired. Please submit again.');
                try {
                  recaptchaRef.current?.reset();
                } catch {}
              }}
              onErrored={() => {
                setError(
                  'reCAPTCHA failed to load. Switching to checkbox mode.'
                );
                setFallbackVisible(true);
              }}
            />
          </RecaptchaBoundary>
        )}

        <button
          className='btn btn--primary'
          type='submit'
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Send Reset Link'}
        </button>

        {/* Helper copy that matches invisible reCAPTCHA behavior */}
        <p className='form-help' style={{ marginTop: 8 }}>
          {fallbackVisible
            ? 'To continue, complete the reCAPTCHA checkbox below and submit again.'
            : 'We use Google reCAPTCHA to protect against spam and abuse. Verification runs automatically and you may be prompted to complete a quick check.'}
        </p>
        <p className='form-help' style={{ marginTop: 4, fontSize: 12 }}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a
            href='https://policies.google.com/privacy'
            target='_blank'
            rel='noopener noreferrer'
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href='https://policies.google.com/terms'
            target='_blank'
            rel='noopener noreferrer'
          >
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </form>
      {message && (
        <p className='form-success' role='status' aria-live='polite'>
          {message}
        </p>
      )}
      {error && (
        <p className='form-error' role='alert' aria-live='assertive'>
          {error}
        </p>
      )}
    </div>
  );
}
